import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { memo, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { stickyHeader } from 'src/components/calendar/styles';
import DialogDetail from 'src/components/task/surgeryCalendar/surgerySchedule/DialogDetail';
import { useStore } from 'src/context/StoreContext';
import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

import { getField } from 'src/common/helper';
import ContentSchedule from 'src/components/task/surgeryCalendar/surgerySchedule/contentSchedule/ContentSchedule';

const days = ['日', '月', '火', '水', '木', '金', '土'];

const colorBorderTableHead = '#DDDDDD';
const colorBorderTableBody = '#F3F3F3';
// const heightLine = 16;

const dataDetailDefine = {
  open: false,
  doctorid: 0,
  date: '',
  reservation_id: '',
};

type dataDetail = {
  open: boolean;
  doctorid: number;
  date: string;
  reservation_id: string;
};

type SchedulesType = {
  doctors: any;
  schedules: any[];
};

type SurgeryScheduleProps = {
  openFilter: boolean;
  dateSelected: string;
  yearMonth: string;
};

const SurgerySchedule: React.FC<SurgeryScheduleProps> = (props) => {
  const storeContext = useStore();
  const store = storeContext.store;
  const setOptionFilter = storeContext.setOptionFilter;
  const optionFilterSelected = storeContext.optionFilterSelected;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<SchedulesType>();
  const [dataShowDetail, setDataShowDetail] =
    useState<dataDetail>(dataDetailDefine);
  const [fields, setFields] = useState<any>({});

  const formatDoctorsData = (data: any[]) => {
    const doctors = data;

    const doctorsObject: any = doctors
      ? doctors.reduce((acc: any, doctor: any) => {
          acc[doctor.id] = doctor;
          return acc;
        }, {} as any)
      : {};

    return doctorsObject;
  };

  const checkDiffMonth = (weekData: any[], indexDay: number) => {
    const nextDate = weekData[indexDay + 1] ? weekData[indexDay + 1].day : '';
    const prevDate = weekData[indexDay - 1] ? weekData[indexDay - 1].day : '';
    const date = weekData[indexDay] ? weekData[indexDay].day : '';

    return (
      ((nextDate !== '' &&
        dayjs(nextDate).format('YYYY-MM') === props.yearMonth) ||
        (prevDate !== '' &&
          dayjs(prevDate).format('YYYY-MM') === props.yearMonth)) &&
      dayjs(date).format('YYYY-MM') !== props.yearMonth
    );
  };

  const checkDateDiffMonth = (date: string) => {
    return dayjs(date).format('YYYY-MM') !== props.yearMonth;
  };

  const getCalendarMonth = async (yearMonth: string, filterSelected: number[]) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const doctorParams = filterSelected.reduce((result: string, doctor: any) => {
        return `${result}&doctorid[]=${doctor}`
      }, "")

      const res = await getAPI(
        `/op-preparation/status?token=${token}&month=${yearMonth}${doctorParams}&dates=`
      );
      if (res && res.code === 200) {
        setSchedules({
          doctors: res.data.doctors ? formatDoctorsData(res.data.doctors) : [],
          schedules: res.data.schedules ? res.data.schedules : [],
        });
        getOptionFilter(res.data.doctors ? res.data.doctors : []);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getOptionFilter = (doctors: any[]) => {
    const newOptionFilter = doctors.map((item: any) => ({
      id: getField(item, 'id', 0),
      name: getField(item, 'name', '-'),
      type: 'doctor',
      color: getField(item, 'bgcolor', '-'),
    }));

    setOptionFilter(newOptionFilter);
  };

  const handleOpenDetail = (reservation_id: string) => {
    setDataShowDetail({
      ...dataDetailDefine,
      reservation_id: reservation_id,
      open: true,
    });
  };

  const handleCloseDetail = () => {
    setDataShowDetail({
      ...dataDetailDefine,
      reservation_id: '',
      open: false,
    });
  };

  const handleSubmit = () => {
    getCalendarMonth(props.yearMonth, optionFilterSelected);
    setDataShowDetail({
      ...dataDetailDefine,
      reservation_id: '',
      open: false,
    });
  }

  useEffect(() => {
    setIsLoading(true);
    getCalendarMonth(props.yearMonth, optionFilterSelected);

        
    const token = getToken();
    getAPI(`/op-preparation/metafields?token=${token}`)
      .then((res) => {
        if (res.code === 200) {
          setFields(res.data);
        }
      })
      .finally(() => {
        // setCheckAPI(true);
      });
    // eslint-disable-next-line
  }, [props.yearMonth, optionFilterSelected]);

  return (
    <Paper
      sx={{
        bgcolor: store.palette.container.paper,
        borderRadius: '16px',
        mt: '10px',
        height: 'calc(100vh - 150px)',
        width: props.openFilter ? 'calc(100vw - 355px)' : 'calc(100vw - 190px)',
        overflow: 'hidden',
      }}
    >
      {!isLoading ? (
        <TableContainer
          sx={{
            boxShadow: 'none',
            '& *': { color: 'primary.dark', fontSize: '12px' },
            width: '100%',
            minWidth: 'calc(100vw - 355px)',
            height: '100%',
          }}
        >
          <Table
            stickyHeader
            sx={{
              '& .MuiTableCell-root.MuiTableCell-head': {
                borderBottom: `1px solid ${colorBorderTableHead}`,
                borderRight: `1px solid ${colorBorderTableHead}`,
                fontWeight: 700,
                fontSize: '12px',
                p: '0px 8px',
                overflow: 'auto',
                bgcolor: '#E6E9ED',
              },
              '& .MuiTableCell-root': {
                borderRight: `1px solid ${colorBorderTableBody}`,
                borderBottom: `1px solid ${colorBorderTableBody}`,
              },
              '& .MuiTableCell-root.MuiTableCell-body': {
                p: '0px',
                bgcolor: '#FFFFFF',
              },
            }}
            aria-label='simple table'
          >
            <TableHead>
              <TableRow sx={{ height: '36px', ...stickyHeader }}>
                {days.map((day, index: number) => (
                  <TableCell
                    key={index}
                    align='center'
                    sx={{ minWidth: '190px' }}
                  >
                    <Typography
                      sx={{
                        fontWeight: '400',
                        lineHeight: '14px',
                        fontSize: '14px',
                        color:
                          index === 0
                            ? '#FC6E51'
                            : index === days.length - 1
                            ? '#5D9CEC'
                            : '#656D78',
                      }}
                      component='pre'
                    >
                      {day}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {schedules &&
                schedules.schedules &&
                schedules.schedules.map((weekData: any[], index: number) => (
                  <TableRow hover key={index}>
                    {weekData.map((dayData: any, indexDay: number) => (
                      <TableCell
                        key={`${dayData.day}${indexDay}`}
                        sx={{
                          fontSize: '12px', 
                          fontWeight: 400,
                          lineWeight: '14.4px',
                          verticalAlign: 'top',
                          position: 'relative',
                          overflow: 'visible',
                          height: '170px',
                          bgcolor:
                            props.dateSelected === dayData.day
                              ? '#E5F6FD !important'
                              : '',
                        }}
                      >
                        <Box
                          sx={{
                            mt: '5px',
                            mb:
                              props.dateSelected === dayData.day
                                ? '6px'
                                : '10px',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              ...(props.dateSelected === dayData.day
                                ? {
                                    bgcolor: '#23B7E5',
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '20px',
                                  }
                                : {}),
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: '700',
                                lineHeight: '12px',
                                fontSize: '12px',
                                alignContent: 'center',
                                color: checkDateDiffMonth(dayData.day)
                                  ? '#CCD1D9'
                                  : props.dateSelected === dayData.day
                                  ? '#FFFFFF'
                                  : indexDay === 0
                                  ? '#FC6E51'
                                  : indexDay === weekData.length - 1
                                  ? '#5D9CEC'
                                  : '#434154',
                              }}
                              component='label'
                            >
                              {checkDiffMonth(weekData, indexDay)
                                ? dayjs(dayData.day).format('M/D')
                                : dayData.label}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1px',
                            mb: '10px',
                          }}
                        >
                          <ContentSchedule
                            key={index}
                            doctors={schedules.doctors}
                            reserves={dayData.reserves}
                            handleOpenDetail={handleOpenDetail}
                            fields={fields || null}
                          />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Skeleton variant='rectangular' width={'100%'} height={760} />
      )}
      <DialogDetail
        open={dataShowDetail.open}
        date={dataShowDetail.date}
        doctorid={dataShowDetail.doctorid}
        reservation_id={dataShowDetail.reservation_id}
        onClose={handleCloseDetail}
        handleSubmit={handleSubmit}
        fields={fields}
      />
    </Paper>
  );
};

export default memo(SurgerySchedule);
